import React, { useEffect, useState, useMemo } from 'react';
import { Plus, ArrowRight, Trophy, Trash2, X, Users, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import Pagination from '../components/Pagination';
import PageHeader from '../components/PageHeader';
import { apiGet, apiPost } from '../lib/api';
import { inrCompact, formatDate, statusColor, inr } from '../lib/format';
import { toast } from 'sonner';
import { useFormFieldHelpers } from '../hooks/useFormFieldHelpers';
import { MaterialPicker } from '../components/pickers/MaterialPicker';
import POImportWizard from '../components/procurement/POImportWizard';
import { useAuth } from '../contexts/AuthContext';
import { VendorPerformance } from './VendorPerformance';
import { VendorBandBadge } from '../components/VendorBandBadge';
import { RateIntelligence } from './RateIntelligence';
import RFQNegotiationDrawer from './procurement/RFQNegotiationDrawer';


const canApproveMPR = (user) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  const dept = (user.department || '').toLowerCase();
  const role = (user.role || '').toLowerCase();
  if (['md', 'ceo', 'cmd', 'cfo'].includes(dept) || ['md', 'ceo', 'cmd', 'cfo'].includes(role)) return true;
  return dept === 'pm' || role === 'pm' || dept === 'project manager';
};

const mprStatusTag = (s) => {
  const map = {
    'Draft': 'Pending',
    'Pending PM Approval': 'Pending',
    'PM Approved': 'Approved',
    'RFQ Issued': 'In Progress',
    'Awarded': 'In Progress',
    'Closed': 'In Progress',
  };
  return statusColor(map[s] || 'Pending');
};

const TABS = ['MPR', 'RFQ', 'Comparative Statement', 'Purchase Orders', 'GRN', 'Vendor Performance', 'Rate Intelligence', 'Approval Matrix'];

export default function Procurement() {
  const [tab, setTab] = useState('MPR');
  const [mprs, setMprs] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [activeRfq, setActiveRfq] = useState(null);
  const [matrix, setMatrix] = useState([]);

  const load = async () => {
    try {
      setMprs((await apiGet('/mpr'))?.items || []);
      setRfqs(await apiGet('/rfq'));
      setPos((await apiGet('/po'))?.items || []);
      setGrns((await apiGet('/grn'))?.items || []);
      const m = await apiGet('/approval-matrix');
      setMatrix(m.tiers || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === 'Comparative Statement' && rfqs.length && !activeRfq) setActiveRfq(rfqs[0].id); }, [tab, rfqs, activeRfq]);

  return (
    <div>
      <PageHeader
        overline="Procurement"
        title="MPR → RFQ → CS → PO Workflow"
        subtitle="Approval-matrix-driven procurement with auto-generated comparative statements."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <KCard label="MPRs Pending PM" value={mprs.filter(m=>m.status==='Pending PM Approval').length} />
        <KCard label="Open RFQs" value={rfqs.filter(r=>r.status==='Open').length} />
        <KCard label="Pending POs" value={pos.filter(p=>p.status==='Pending').length} />
        <KCard label="Total PO Value" value={inrCompact(pos.reduce((s,p)=>s+(p.value||0),0))} />
      </div>

      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map(t => (
            <button key={t} onClick={()=>setTab(t)} data-testid={`proc-tab-${t.toLowerCase().replace(/\s+/g,'-')}`}
              className={`relative px-5 py-3 text-sm font-display font-semibold whitespace-nowrap transition-colors ${tab === t ? 'text-[#0F172A]' : 'text-slate-500 hover:text-[#0F172A]'}`}>
              {t}
              {tab === t && <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#1F7A6E]" />}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'MPR' && <MPRPanel mprs={mprs} reload={load} setTab={setTab} setActiveRfq={setActiveRfq} />}
          {tab === 'RFQ' && <RFQPanel rfqs={rfqs} reload={load} onView={(id)=>{ setActiveRfq(id); setTab('Comparative Statement'); }} />}
          {tab === 'Comparative Statement' && <CSPanel rfqId={activeRfq} setRfqId={setActiveRfq} rfqs={rfqs} reload={load} setTab={setTab} />}
          {tab === 'Purchase Orders' && <POPanel pos={pos} reload={load} />}
          {tab === 'GRN' && <GRNPanel pos={pos} grns={grns} reload={load} />}
          {tab === 'Vendor Performance' && <VendorPerformance />}
          {tab === 'Rate Intelligence' && <RateIntelligence />}
          {tab === 'Approval Matrix' && <ApprovalMatrix matrix={matrix} />}
        </div>
      </div>
    </div>
  );
}

const KCard = ({ label, value }) => (
  <div className="bg-white border border-slate-200 rounded-sm p-4">
    <div className="kpi-overline">{label}</div>
    <div className="font-display text-2xl font-extrabold tracking-tight mt-1">{value}</div>
  </div>
);

// ----- MPR -----
const MPRPanel = ({ mprs, reload, setTab, setActiveRfq }) => {
  const { user } = useAuth();
  const isApprover = canApproveMPR(user);
  const [convertingId, setConvertingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState('Tata Steel, JSW Steel, Vizag Steel');
  const [rcMatches, setRcMatches] = useState([]);

  // Filters + sort + toggle + pagination
  const [activeTab, setActiveTab] = useState('active');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMPR, setSelectedMPR] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  useEffect(() => { apiGet('/vendors', { limit: 200 }).then(d => setVendors(d?.items || [])).catch(()=>{}); }, []);

  useEffect(() => {
    if (!convertingId) { setRcMatches([]); return; }
    const names = selectedVendors.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
    const matched = vendors.filter(v => names.includes((v.name || '').toLowerCase())).slice(0, 5);
    if (!matched.length) { setRcMatches([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      const found = [];
      for (const v of matched) {
        try {
          const chk = await apiGet('/rfq/rate-contract-check', { vendor_id: v.id });
          if (chk?.can_skip_rfq) found.push({ vendor: v.name, rc: chk.rate_contract });
        } catch {}
      }
      if (!cancelled) setRcMatches(found);
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [convertingId, selectedVendors, vendors]);

  const submitForApproval = async (m) => {
    setBusyId(m.id);
    try {
      await apiPost(`/mpr/${m.id}/submit`, {});
      toast.success('Sent to Project Manager for approval');
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to submit'); }
    finally { setBusyId(null); }
  };

  const approve = async (m) => {
    setBusyId(m.id);
    try {
      await apiPost(`/mpr/${m.id}/pm-approve`, {});
      toast.success('MPR approved');
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to approve'); }
    finally { setBusyId(null); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) { toast.error('A rejection reason is required'); return; }
    setBusyId(rejectingId);
    try {
      await apiPost(`/mpr/${rejectingId}/pm-reject`, { reason: rejectReason.trim() });
      toast.success('MPR rejected — returned to Draft');
      setRejectingId(null); setRejectReason('');
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to reject'); }
    finally { setBusyId(null); }
  };

  const convert = async (mpr) => {
    try {
      const list = selectedVendors.split(',').map(s=>s.trim()).filter(Boolean);
      const rfq = await apiPost(`/mpr/${mpr.id}/convert-to-rfq`, { vendors: list });
      toast.success(`RFQ ${rfq.rfq_no} created`);
      setConvertingId(null);
      reload();
      setActiveRfq(rfq.id);
      setTab('RFQ');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to convert'); }
  };

  // ── Helpers: normalize both old and new MPR schemas ──
  const mprProject = (m) => m.project_name || m.project || '—';
  const mprItems = (m) => {
    if (m.line_items?.length) return m.line_items;
    if (m.item) return [{ material_name: m.item, qty_required: m.quantity, unit: m.uom || m.unit }];
    return [];
  };
  const mprItemSummary = (m) => {
    const items = mprItems(m);
    if (!items.length) return '—';
    if (items.length === 1) return `${items[0].material_name || '—'} · ${items[0].qty_required || m.quantity || ''} ${items[0].unit || m.uom || ''}`.trim();
    return `${items.length} items`;
  };

  // ── Active / Completed ──
  const COMPLETED = new Set(['closed', 'Closed', 'Fully Received', 'fully received']);
  const tabMprs = mprs.filter(m => activeTab === 'completed' ? COMPLETED.has(m.status || '') : !COMPLETED.has(m.status || ''));

  // ── Unique options ──
  const projectOptions = [...new Set(tabMprs.map(mprProject).filter(Boolean))].sort();
  const statusOptions  = [...new Set(tabMprs.map(m => m.status || '').filter(Boolean))].sort();

  // ── Filter ──
  const q = filterSearch.trim().toLowerCase();
  const filtered = tabMprs.filter(m => {
    if (filterProject && mprProject(m) !== filterProject) return false;
    if (filterStatus  && (m.status || '') !== filterStatus) return false;
    if (q) {
      const hay = `${m.mpr_no} ${mprProject(m)} ${mprItems(m).map(i => i.material_name || '').join(' ')}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // ── Sort ──
  const sorted = [...filtered].sort((a, b) => {
    // Normalize values — handle both old (project/item) and new (project_name/line_items) schemas
    const val = (m, key) => {
      if (key === 'project_name') return mprProject(m);
      if (key === 'item_summary') return mprItemSummary(m);
      return m[key] || '';
    };
    const va = val(a, sortKey), vb = val(b, sortKey);
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Pagination — reset to page 1 whenever filters/tab/sort change
  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const paginated = sorted.slice(start, start + PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };
  const SortIcon = ({ k }) => sortKey !== k
    ? <span className="text-slate-400 ml-0.5 text-[10px]">↕</span>
    : <span className="text-[#1F7A6E] ml-0.5 text-[10px] font-bold">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  const pendingCount = mprs.filter(m=>m.status==='Pending PM Approval').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Material Requisition Plan</div>
          <h3 className="font-display font-bold text-base mt-1">{sorted.length} of {mprs.length} MPRs · {pendingCount} pending PM approval</h3>
        </div>
        <span className="text-[10px] text-slate-400 font-display uppercase tracking-wider" data-testid="mpr-create-moved-hint">MPRs are raised in Store &amp; Inventory → MPR</span>
      </div>

      {/* ── Active / Completed toggle ── */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit mb-3">
        {[['active','Active'],['completed','Completed']].map(([val,label]) => (
          <button key={val} onClick={() => { setActiveTab(val); setFilterStatus(''); setFilterProject(''); setFilterSearch(''); setPage(1); }}
            className={`px-4 py-1.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-md transition-colors ${activeTab===val ? 'bg-white text-[#1F7A6E] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`mpr-tab-${val}`}>
            {label} ({mprs.filter(m => val==='completed' ? COMPLETED.has(m.status||'') : !COMPLETED.has(m.status||'')).length})
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <input type="search" placeholder="Search MPR no, project, material…"
          value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#1F7A6E] w-60"
          data-testid="mpr-filter-search" />
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#1F7A6E] bg-white"
          data-testid="mpr-filter-project">
          <option value="">All Projects</option>
          {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-[#1F7A6E] bg-white"
          data-testid="mpr-filter-status">
          <option value="">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filterSearch || filterProject || filterStatus) && (
          <button onClick={() => { setFilterSearch(''); setFilterProject(''); setFilterStatus(''); setPage(1); }}
            className="text-[10px] text-slate-500 hover:text-rose-600 underline">Clear</button>
        )}
      </div>

      <table className="w-full dense-table">
        <thead>
          <tr>
            <th className="cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('mpr_no')}>MPR No <SortIcon k="mpr_no" /></th>
            <th className="cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('created_at')}>Date Created <SortIcon k="created_at" /></th>
            <th className="cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('project_name')}>Project <SortIcon k="project_name" /></th>
            <th>Item(s)</th>
            <th>Plan Freq.</th>
            <th>Basis</th>
            <th>Required By</th>
            <th className="cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('status')}>Status <SortIcon k="status" /></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && <tr><td colSpan="9" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">
            {(filterSearch||filterProject||filterStatus) ? 'No results match the filters' : activeTab==='completed' ? 'No completed MPRs' : 'No active MPRs'}
          </td></tr>}
          {paginated.map(m => {
            const items = mprItems(m);
            const createdAt = m.created_at ? new Date(m.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';
            return (
              <tr key={m.id} data-testid={`mpr-row-${m.id}`}
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setSelectedMPR(m)}>
                <td className="font-mono text-xs">{m.mpr_no}</td>
                <td className="text-xs whitespace-nowrap">{createdAt}</td>
                <td className="max-w-[180px] truncate" title={mprProject(m)}>{mprProject(m)}</td>
                <td className="text-xs">
                  {items.length > 1
                    ? <span className="font-display font-semibold text-[#1F7A6E]">{items.length} items</span>
                    : mprItemSummary(m)}
                </td>
                <td className="text-xs">{m.plan_frequency || '—'}</td>
                <td className="text-xs">{m.source_basis || '—'}</td>
                <td>{formatDate(m.required_by)}</td>
                <td>
                  <span className={`tag ${mprStatusTag(m.status)}`} data-testid={`mpr-status-${m.id}`}>{m.status}</span>
                  {m.status === 'Draft' && m.rejection_reason && (
                    <div className="text-[10px] text-rose-600 mt-0.5" title={m.rejection_reason}>Rejected: {m.rejection_reason}</div>
                  )}
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex items-center gap-2 justify-end">
                    {(m.status === 'Draft') && (
                      <button data-testid={`submit-mpr-btn-${m.id}`} disabled={busyId===m.id} onClick={()=>submitForApproval(m)} className="text-xs text-[#1F7A6E] font-display font-semibold hover:underline disabled:opacity-40">Submit for Approval</button>
                    )}
                    {(m.status === 'Pending PM Approval') && isApprover && (
                      <>
                        <button data-testid={`approve-mpr-btn-${m.id}`} disabled={busyId===m.id} onClick={()=>approve(m)} className="text-xs text-emerald-700 font-display font-semibold hover:underline disabled:opacity-40">Approve</button>
                        <button data-testid={`reject-mpr-btn-${m.id}`} disabled={busyId===m.id} onClick={()=>{setRejectingId(m.id); setRejectReason('');}} className="text-xs text-rose-600 font-display font-semibold hover:underline disabled:opacity-40">Reject</button>
                      </>
                    )}
                    {(m.status === 'Pending PM Approval') && !isApprover && (
                      <span className="text-[10px] text-amber-600 font-display uppercase tracking-wider">Awaiting PM</span>
                    )}
                    {(m.status === 'PM Approved') && (
                      <button data-testid={`convert-mpr-btn-${m.id}`} onClick={()=>setConvertingId(m.id)} className="text-xs text-[#1F7A6E] font-display font-semibold flex items-center gap-1 hover:underline">Convert to RFQ <ArrowRight size={12} /></button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalFiltered > PAGE_SIZE && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          <Pagination
            page={safePage} pageSize={PAGE_SIZE} total={totalFiltered}
            start={start} end={Math.min(start + PAGE_SIZE, totalFiltered)}
            onPageChange={setPage} onPageSizeChange={() => {}}
            pageSizeOptions={[]} label="MPRs"
            data-testid="mpr-pagination"
          />
        </div>
      )}

      {/* ── MPR Detail Modal ── */}
      {selectedMPR && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedMPR(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-white rounded-sm shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">MPR Detail</div>
                <h3 className="font-display font-bold text-lg mt-0.5 font-mono">{selectedMPR.mpr_no}</h3>
              </div>
              <button onClick={() => setSelectedMPR(null)} className="text-slate-400 hover:text-slate-700 mt-1"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><span className="kpi-overline">Project</span><p className="mt-0.5">{mprProject(selectedMPR)}</p></div>
                <div><span className="kpi-overline">Status</span><p className="mt-0.5"><span className={`tag ${mprStatusTag(selectedMPR.status)}`}>{selectedMPR.status}</span></p></div>
                <div><span className="kpi-overline">Date Created</span><p className="mt-0.5">{selectedMPR.created_at ? new Date(selectedMPR.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—'}</p></div>
                <div><span className="kpi-overline">Required By</span><p className="mt-0.5">{formatDate(selectedMPR.required_by) || '—'}</p></div>
                <div><span className="kpi-overline">Plan Frequency</span><p className="mt-0.5">{selectedMPR.plan_frequency || '—'}</p></div>
                <div><span className="kpi-overline">Source Basis</span><p className="mt-0.5">{selectedMPR.source_basis || '—'}</p></div>
                {selectedMPR.requested_by && <div><span className="kpi-overline">Requested By</span><p className="mt-0.5">{selectedMPR.requested_by}</p></div>}
                {selectedMPR.approved_by && <div><span className="kpi-overline">Approved By</span><p className="mt-0.5">{selectedMPR.approved_by}</p></div>}
              </div>
              {selectedMPR.justification && (
                <div><span className="kpi-overline">Justification</span><p className="mt-0.5 text-sm text-slate-700">{selectedMPR.justification}</p></div>
              )}
              <div>
                <div className="kpi-overline mb-2">Line Items ({mprItems(selectedMPR).length})</div>
                <table className="w-full dense-table">
                  <thead><tr><th>#</th><th>Material</th><th>Category</th><th className="text-right">Qty</th><th>Unit</th><th>Remarks</th></tr></thead>
                  <tbody>
                    {mprItems(selectedMPR).map((item, idx) => (
                      <tr key={idx}>
                        <td className="text-xs text-slate-500">{idx + 1}</td>
                        <td className="font-medium text-sm">{item.material_name || item.item || '—'}</td>
                        <td className="text-xs">{item.category || '—'}</td>
                        <td className="text-right tabular-nums font-display font-bold">{item.qty_required ?? item.quantity ?? '—'}</td>
                        <td className="text-xs">{item.unit || item.uom || '—'}</td>
                        <td className="text-xs text-slate-500">{item.remarks || '—'}</td>
                      </tr>
                    ))}
                    {mprItems(selectedMPR).length === 0 && (
                      <tr><td colSpan="6" className="text-center text-slate-400 text-xs py-4">No items</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
              {selectedMPR.status === 'Draft' && (
                <button onClick={() => { submitForApproval(selectedMPR); setSelectedMPR(null); }}
                  className="px-4 py-2 text-xs bg-[#1F7A6E] text-white rounded-sm font-display font-semibold">Submit for Approval</button>
              )}
              {selectedMPR.status === 'Pending PM Approval' && isApprover && (
                <>
                  <button onClick={() => { approve(selectedMPR); setSelectedMPR(null); }}
                    className="px-4 py-2 text-xs bg-emerald-600 text-white rounded-sm font-display font-semibold">Approve</button>
                  <button onClick={() => { setRejectingId(selectedMPR.id); setRejectReason(''); setSelectedMPR(null); }}
                    className="px-4 py-2 text-xs bg-rose-600 text-white rounded-sm font-display font-semibold">Reject</button>
                </>
              )}
              {selectedMPR.status === 'PM Approved' && (
                <button onClick={() => { setConvertingId(selectedMPR.id); setSelectedMPR(null); }}
                  className="px-4 py-2 text-xs bg-[#1F7A6E] text-white rounded-sm font-display font-semibold flex items-center gap-1">Convert to RFQ <ArrowRight size={12} /></button>
              )}
              <button onClick={() => setSelectedMPR(null)} className="px-4 py-2 text-xs border border-slate-300 rounded-sm font-display hover:bg-slate-50">Close</button>
            </div>
          </div>
        </div>
      )}

      {convertingId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div onClick={e=>e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-md">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">Convert</div>
            <h3 className="font-display font-bold text-xl mt-1 mb-4">MPR → RFQ</h3>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-2">Vendors to invite (comma separated)</label>
            <textarea value={selectedVendors} onChange={e=>setSelectedVendors(e.target.value)} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#1F7A6E] focus:ring-1 focus:ring-[#1F7A6E] focus:outline-none" />
            {rcMatches.length > 0 && (
              <div className="mt-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="rate-contract-banner">
                {rcMatches.map((m, i) => (
                  <div key={i} className="text-xs text-emerald-800">
                    ✓ <span className="font-display font-bold">{m.vendor}</span> has an active rate contract ({m.rc?.description}) — rates are pre-agreed; you may proceed to a direct PO/WO without a full RFQ.
                  </div>
                ))}
              </div>
            )}
            <div className="text-xs text-slate-500 mt-2">Empanelled: {vendors.map(v=>v.name).join(', ')}</div>
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
              <button onClick={()=>setConvertingId(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button data-testid="confirm-convert-btn" onClick={()=>convert(mprs.find(x=>x.id===convertingId))} className="px-4 py-2 text-sm bg-[#1F7A6E] text-white rounded-sm font-display font-semibold">Issue RFQ</button>
            </div>
          </div>
        </div>
      )}

      {rejectingId && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div onClick={e=>e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-md">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-rose-600">Reject MPR</div>
            <h3 className="font-display font-bold text-xl mt-1 mb-4">Return to Stores for re-work</h3>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-2">Reason for rejection *</label>
            <textarea data-testid="mpr-reject-reason" value={rejectReason} onChange={e=>setRejectReason(e.target.value)} rows={3} placeholder="e.g. Quantity too high for this period — split into weekly plan" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 focus:outline-none" />
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
              <button onClick={()=>{setRejectingId(null); setRejectReason('');}} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button data-testid="confirm-reject-btn" disabled={busyId===rejectingId} onClick={reject} className="px-4 py-2 text-sm bg-rose-600 text-white rounded-sm font-display font-semibold disabled:opacity-40">Reject MPR</button>
            </div>
          </div>
        </div>
      )}

      {/* MPR creation moved to the Store & Inventory module */}
    </div>
  );
};

// ----- RFQ -----
const RFQPanel = ({ rfqs, reload, onView }) => {
  const [quoteOf, setQuoteOf] = useState(null);
  const [quote, setQuote] = useState({});
  const [negotiating, setNegotiating] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    await apiPost(`/rfq/${quoteOf}/quotes`, { ...quote, rate: Number(quote.rate||0), delivery_days: Number(quote.delivery_days||0) });
    toast.success('Quote added');
    setQuoteOf(null); setQuote({});
    reload();
  };

  return (
    <div>
      <div className="kpi-overline mb-3">Request For Quotation</div>
      <table className="w-full dense-table">
        <thead><tr><th>RFQ No</th><th>Project</th><th>Item</th><th className="text-right">Qty</th><th>Vendors</th><th className="text-center">Quotes</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rfqs.length === 0 && <tr><td colSpan="8" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No RFQs · raise from an approved MPR</td></tr>}
          {rfqs.map(r => (
            <tr key={r.id}>
              <td className="font-mono text-xs">{r.rfq_no}</td>
              <td>{r.project}</td>
              <td>{r.item}</td>
              <td className="text-right tabular-nums font-display font-bold">{r.quantity} {r.unit}</td>
              <td className="text-xs">{(r.vendors||[]).join(', ')}</td>
              <td className="text-center font-display font-bold">{(r.quotes||[]).length}/{(r.vendors||[]).length}</td>
              <td>
                <div className="flex items-center gap-1.5">
                  <span className={`tag ${statusColor(r.status === 'Awarded' ? 'Approved' : 'Open')}`}>{r.status}</span>
                  {r.sourcing_route === 'emergency' && (
                    <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${r.justification_approved_by ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}
                      title={r.justification_approved_by ? 'Emergency — justification approved' : 'Emergency — additional approval required before award'}
                      data-testid={`rfq-emergency-badge-${r.id}`}>
                      ⚡ Emergency{!r.justification_approved_by && ' · needs approval'}
                    </span>
                  )}
                </div>
              </td>
              <td>
                <div className="flex gap-2">
                  <button data-testid="rfq-add-quote-btn" onClick={()=>{setQuoteOf(r.id); setQuote({ vendor: r.vendors?.[0]||'' });}} className="text-xs text-slate-500 hover:text-[#1F7A6E] font-display">Add Quote</button>
                  <button data-testid={`rfq-negotiate-btn-${r.id}`} onClick={()=>setNegotiating(r)} className="text-xs text-amber-700 hover:text-amber-800 font-display font-semibold">Negotiate{(r.negotiation_log||[]).length ? ` (${r.negotiation_log.length})` : ''}</button>
                  <button data-testid="rfq-view-cs-btn" onClick={()=>onView(r.id)} className="text-xs text-[#1F7A6E] font-display font-semibold">View CS</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {quoteOf && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={submit} onClick={e=>e.stopPropagation()} className="bg-white rounded-sm p-5 w-full max-w-md grid grid-cols-2 gap-4">
            <div className="col-span-2"><div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">New</div><h3 className="font-display font-bold text-xl mt-1">Vendor Quote</h3></div>
            {['vendor','rate','delivery_days','payment_terms','validity'].map(k => (
              <div key={k} className={k==='vendor'?'col-span-2':''}>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{k.replace(/_/g,' ')}</label>
                <input type={['rate','delivery_days'].includes(k)?'number':'text'} value={quote[k]||''} onChange={e=>setQuote({...quote,[k]:e.target.value})} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#1F7A6E] focus:ring-1 focus:ring-[#1F7A6E] focus:outline-none" required />
              </div>
            ))}
            <div className="col-span-2 flex justify-end gap-2 pt-3 border-t border-slate-200">
              <button type="button" onClick={()=>setQuoteOf(null)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50 font-display">Cancel</button>
              <button type="submit" className="px-4 py-2 text-sm bg-[#1F7A6E] text-white rounded-sm font-display font-semibold">Add Quote</button>
            </div>
          </form>
        </div>
      )}
      {negotiating && (
        <RFQNegotiationDrawer rfq={negotiating} onClose={() => setNegotiating(null)} onChanged={reload} />
      )}
    </div>
  );
};

// ----- Comparative Statement -----
const CSPanel = ({ rfqId, setRfqId, rfqs, reload, setTab }) => {
  const [data, setData] = useState(null);
  const [selVendor, setSelVendor] = useState('');
  const [rationale, setRationale] = useState('');
  const [savingDecision, setSavingDecision] = useState(false);

  const loadComparative = () => {
    if (!rfqId) return;
    apiGet(`/rfq/${rfqId}/comparative`).then((d) => {
      setData(d);
      setSelVendor(d?.committee?.decision?.selected_vendor || '');
      setRationale('');
    }).catch(() => setData(null));
  };

  useEffect(() => { loadComparative(); /* eslint-disable-next-line */ }, [rfqId]);

  if (!rfqId) return <div className="text-slate-400 text-sm py-8 text-center">Select or open an RFQ first.</div>;
  if (!data) return <div className="text-slate-400 text-sm py-8 text-center">Loading…</div>;

  const committee = data.committee || { threshold: 0, required: false, is_member: false, decision: null };
  const decision = committee.decision;
  const committeeRequired = committee.required;
  const fmtThreshold = inr(committee.threshold);

  const matchesDecision = (q) =>
    decision && [q.vendor, q.vendor_name, q.vendor_id].some(
      (k) => k && [decision.selected_vendor, decision.selected_vendor_name, decision.selected_vendor_id].includes(k)
    );

  const award = async (vendor) => {
    if (!window.confirm(`Award PO to ${vendor}?`)) return;
    try {
      await apiPost(`/rfq/${rfqId}/award`, { vendor });
      toast.success(`PO created for ${vendor}`);
      reload(); setTab('Purchase Orders');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not award PO');
    }
  };

  const recordDecision = async () => {
    if (!selVendor) { toast.error('Select the vendor the committee chose'); return; }
    if (!rationale.trim()) { toast.error('Add a short rationale for the committee decision'); return; }
    setSavingDecision(true);
    try {
      await apiPost(`/rfq/${rfqId}/committee-decision`, { selected_vendor: selVendor, rationale: rationale.trim() });
      toast.success('Committee decision recorded');
      loadComparative();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not record committee decision');
    } finally {
      setSavingDecision(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="kpi-overline">Comparative Statement</div>
          <h3 className="font-display font-bold text-base mt-1">{data.rfq.rfq_no} · {data.rfq.item}</h3>
        </div>
        <select value={rfqId} onChange={e=>setRfqId(e.target.value)} className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white">
          {rfqs.map(r => <option key={r.id} value={r.id}>{r.rfq_no} — {r.item}</option>)}
        </select>
      </div>

      {/* Committee vendor-selection (above-threshold orders only) */}
      {committeeRequired && (
        <div data-testid="committee-panel" className="mb-4 border border-amber-300 bg-amber-50/60 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={15} className="text-amber-700" />
            <span className="font-display font-bold text-sm text-amber-900">Committee Vendor Selection required</span>
            <span className="text-[11px] text-amber-700">(order above {fmtThreshold})</span>
          </div>
          {decision ? (
            <div data-testid="committee-decision-recorded" className="text-sm text-slate-700">
              <div className="font-semibold text-emerald-800">Committee selected: {decision.selected_vendor_name}</div>
              <div className="text-xs mt-1 text-slate-600 italic">“{decision.rationale}”</div>
              <div className="text-[11px] mt-1 text-slate-500">
                Recorded by {decision.decided_by} · {formatDate(decision.decided_at)}
              </div>
            </div>
          ) : committee.is_member ? (
            <div className="space-y-2">
              <p className="text-xs text-amber-800">As a committee member, record which vendor was selected from the comparative below, with a rationale. The order can only be awarded to the selected vendor.</p>
              <div className="grid md:grid-cols-2 gap-2">
                <select
                  data-testid="committee-vendor-select"
                  value={selVendor}
                  onChange={e=>setSelVendor(e.target.value)}
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
                >
                  <option value="">Select chosen vendor…</option>
                  {data.comparative.map(q => (
                    <option key={q.vendor} value={q.vendor}>{q.rank} · {q.vendor} · {inrCompact(q.total_value)}</option>
                  ))}
                </select>
                <input
                  data-testid="committee-rationale"
                  value={rationale}
                  onChange={e=>setRationale(e.target.value)}
                  placeholder="Rationale (e.g. best delivery + compliance)"
                  className="px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
              </div>
              <button
                data-testid="committee-record-btn"
                onClick={recordDecision}
                disabled={savingDecision}
              className="text-xs px-3 py-1.5 bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] font-display font-semibold disabled:opacity-50"
              >
                {savingDecision ? 'Saving…' : 'Record Committee Decision'}
              </button>
            </div>
          ) : (
            <p className="text-xs text-amber-800">Awaiting the selection committee to record their chosen vendor. You are not a committee member.</p>
          )}
        </div>
      )}

      <table className="w-full dense-table">
        <thead><tr>
          <th>Rank</th><th>Vendor</th><th className="text-right">Rate</th><th className="text-right">Total Value</th>
          <th>Delivery</th><th>Terms</th><th>Approver</th><th></th>
        </tr></thead>
        <tbody>
          {data.comparative.length === 0 && <tr><td colSpan="8" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No quotes received yet</td></tr>}
          {data.comparative.map((q, i) => {
            const isSelected = matchesDecision(q);
            // Below threshold → award L1 directly (as before). Above threshold →
            // award only the committee-selected vendor, and only after a decision.
            const showAward = committeeRequired ? (decision && isSelected) : (i === 0);
            const blockedNoDecision = committeeRequired && !decision && i === 0;
            return (
              <tr key={q.vendor} className={isSelected ? 'bg-amber-50/50' : i === 0 ? 'bg-emerald-50/30' : ''}>
                <td>
                  <span className={`tag ${i===0?'bg-emerald-50 text-emerald-700 border border-emerald-200':'bg-slate-100 text-slate-600 border border-slate-200'} flex items-center gap-1 w-fit`}>
                    {i === 0 && <Trophy size={11} />}{q.rank}
                  </span>
                </td>
                <td className="font-display font-bold">
                  {q.vendor}
                  {q.performance_band && <span className="ml-2"><VendorBandBadge band={q.performance_band} /></span>}
                  {isSelected && <span className="ml-2 tag bg-amber-100 text-amber-800 border border-amber-200">Committee pick</span>}
                </td>
                <td className="text-right tabular-nums font-mono">{inr(q.rate)}</td>
                <td className="text-right tabular-nums font-display font-bold">{inrCompact(q.total_value)}</td>
                <td className="text-xs">{q.delivery_days} days</td>
                <td className="text-xs text-slate-500">{q.payment_terms}</td>
                <td className="text-xs">
                  <span className="font-display font-semibold">{q.approver_required}</span>
                </td>
                <td>
                  {showAward && (
                    <button data-testid="award-po-btn" onClick={()=>award(q.vendor)} className="text-xs px-3 py-1.5 bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] font-display font-semibold">Award PO</button>
                  )}
                  {blockedNoDecision && (
                    <button
                      data-testid="award-po-disabled"
                      disabled
                      title={`Committee must select the vendor for orders above ${fmtThreshold}`}
                      className="text-xs px-3 py-1.5 bg-slate-200 text-slate-400 rounded-sm font-display font-semibold cursor-not-allowed"
                    >
                      Award PO
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ----- PO Panel -----
const POPanel = ({ pos, reload }) => {
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [notesPo, setNotesPo] = useState(null);
  const approve = async (id) => {
    await apiPost(`/po/${id}/approve`, {});
    toast.success('PO approved'); reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="kpi-overline">Purchase Orders</div>
        <div className="flex items-center gap-2">
          <button onClick={() => setImporting(true)} data-testid="po-bulk-upload-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#1F7A6E] text-[#1F7A6E] hover:bg-teal-50 rounded-sm font-display font-semibold">
            <Upload size={13} /> Bulk Upload
          </button>
          <button onClick={() => setCreating(true)} data-testid="manual-po-new-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-semibold">
            <Plus size={13} /> New PO (Manual)
          </button>
        </div>
      </div>
      <table className="w-full dense-table">
        <thead><tr><th>PO No</th><th>Vendor</th><th>Project</th><th className="text-right">Value</th><th>Approver</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {pos.length === 0 && <tr><td colSpan="7" className="text-center py-12 text-slate-400 text-xs uppercase tracking-widest font-display">No POs yet</td></tr>}
          {pos.map(p => (
            <tr key={p.id}>
              <td className="font-mono text-xs">{p.po_no}</td>
              <td>{p.vendor}</td>
              <td>{p.project}</td>
              <td className="text-right tabular-nums font-display font-bold">{inrCompact(p.value)}</td>
              <td className="text-xs">{p.approver_required || '—'}</td>
              <td><span className={`tag ${statusColor(p.status)}`}>{p.status}</span></td>
              <td className="flex items-center gap-2">
                {p.status === 'Pending' && (
                  <button data-testid="approve-po-btn" onClick={()=>approve(p.id)} className="text-xs px-3 py-1 bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] font-display font-semibold">Approve</button>
                )}
                <button data-testid={`po-notes-btn-${p.id}`} onClick={()=>setNotesPo(notesPo?.id === p.id ? null : p)}
                  className="text-xs px-2 py-1 border border-slate-300 rounded-sm hover:border-[#1F7A6E] hover:text-[#1F7A6E] font-display font-semibold">
                  Notes
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {creating && <ManualPOModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />}
      {importing && <POImportWizard onClose={() => setImporting(false)} onImported={reload} />}
    </div>
  );
};

const GRNPanel = ({ pos, grns, reload }) => {
  const [recordingPo, setRecordingPo] = useState(null);
  const eligible = pos.filter(p => ['Approved', 'Partially Received'].includes(p.status));

  const orderedQtyOf = (p) => {
    if (p.items?.length) return p.items.reduce((s, it) => s + Number(it.qty || it.quantity || 0), 0);
    return Number(p.quantity || 0);
  };

  return (
    <div>
      <div className="kpi-overline mb-3">Pending Receipt · {eligible.length}</div>
      <table className="w-full dense-table mb-6">
        <thead><tr><th>PO No</th><th>Vendor</th><th className="text-right">Ordered</th><th className="text-right">Received</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {eligible.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400 text-xs uppercase tracking-widest font-display">No POs awaiting receipt</td></tr>}
          {eligible.map(p => (
            <tr key={p.id} data-testid={`grn-po-row-${p.id}`}>
              <td className="font-mono text-xs">{p.po_no}</td>
              <td>{p.vendor_name || p.vendor}</td>
              <td className="text-right tabular-nums">{orderedQtyOf(p)}</td>
              <td className="text-right tabular-nums">{p.received_qty || 0}</td>
              <td><span className={`tag ${statusColor(p.status)}`}>{p.status}</span></td>
              <td>
                <button data-testid={`grn-record-btn-${p.id}`} onClick={() => setRecordingPo(p)}
                  className="text-xs px-3 py-1 bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] font-display font-semibold">
                  Record GRN
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="kpi-overline mb-3">Recent GRNs · {grns.length}</div>
      <table className="w-full dense-table">
        <thead><tr><th>GRN No</th><th>PO No</th><th>Vendor</th><th>Item(s)</th><th className="text-right">Value</th><th>Received</th></tr></thead>
        <tbody>
          {grns.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400 text-xs uppercase tracking-widest font-display">No GRNs recorded yet</td></tr>}
          {grns.map(g => (
            <tr key={g.id} data-testid={`grn-row-${g.id}`}>
              <td className="font-mono text-xs">{g.grn_no}</td>
              <td className="font-mono text-xs">{g.po_no || <span className="text-slate-400 italic">Non-PO</span>}</td>
              <td>{g.vendor_name}</td>
              <td className="text-xs">{g.item || (g.items ? `${g.items.length} item(s)` : '—')}</td>
              <td className="text-right tabular-nums font-display font-bold">{inrCompact(g.total_value)}</td>
              <td className="text-xs">{formatDate(g.received_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {recordingPo && (
        <RecordGRNModal po={recordingPo} orderedQty={orderedQtyOf(recordingPo)}
          onClose={() => setRecordingPo(null)}
          onSaved={() => { setRecordingPo(null); reload(); }} />
      )}
    </div>
  );
};

function RecordGRNModal({ po, orderedQty, onClose, onSaved }) {
  const remaining = Math.max(0, orderedQty - Number(po.received_qty || 0));
  const [form, setForm] = useState({
    received_qty: remaining || orderedQty,
    challan_no: '', challan_date: new Date().toISOString().slice(0, 10), remarks: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!(Number(form.received_qty) > 0)) { toast.error('Received quantity must be greater than 0'); return; }
    setSaving(true);
    try {
      await apiPost(`/po/${po.id}/grn`, {
        received_qty: Number(form.received_qty),
        challan_no: form.challan_no.trim() || undefined,
        challan_date: form.challan_date || undefined,
        remarks: form.remarks.trim() || undefined,
      });
      toast.success('GRN recorded');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record GRN');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-md" data-testid="grn-record-modal">
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">Record GRN</div>
            <h3 className="font-display font-bold text-lg mt-1">{po.po_no} · {po.vendor_name || po.vendor}</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
              Received Qty (ordered {orderedQty}, remaining {remaining})
            </label>
            <input type="number" min="0" step="any" value={form.received_qty}
              onChange={e => setForm({ ...form, received_qty: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="grn-received-qty" required />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Challan / DC No.</label>
            <input value={form.challan_no} onChange={e => setForm({ ...form, challan_no: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="grn-challan-no" />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Challan Date</label>
            <input type="date" value={form.challan_date} onChange={e => setForm({ ...form, challan_date: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="grn-challan-date" />
          </div>
          <div>
            <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Remarks</label>
            <textarea value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="grn-remarks" />
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-xs text-slate-600">Cancel</button>
          <button type="submit" disabled={saving} data-testid="grn-record-save"
            className="px-4 py-2 text-xs bg-[#1F7A6E] text-white rounded-sm font-display font-bold disabled:opacity-50">
            {saving ? 'Saving…' : 'Save GRN'}
          </button>
        </div>
      </form>
    </div>
  );
}

function ManualPOModal({ onClose, onSaved }) {
  const ff = useFormFieldHelpers('po_create');
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState({
    vendor_id: '', vendor: '',
    po_date: new Date().toISOString().slice(0, 10),
    delivery_date: '', delivery_address: '',
    items: [{ material_id: '', description: '', qty: 1, unit: 'Nos', rate: 0 }],
    terms: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Vendor options = Vendor Master (incomplete-registration vendors shown but
    // disabled — no new manual POs to them) + legacy subcontractors not yet in
    // the master.
    Promise.all([
      apiGet('/vendors', { limit: 200 }).catch(() => null),
      apiGet('/subcontractors').catch(() => []),
    ]).then(([vm, subs]) => {
      const master = (vm?.items || []).map((v) => ({
        id: v.id, name: v.name, incomplete: v.registration_complete === false,
      }));
      const names = new Set(master.map((m) => (m.name || '').toLowerCase()));
      const extra = (subs || [])
        .filter((s) => !names.has((s.name || '').toLowerCase()))
        .map((s) => ({ id: s.id, name: s.name, incomplete: false }));
      setVendors([...master, ...extra].sort((a, b) => (a.name || '').localeCompare(b.name || '')));
    });
  }, []);

  const lineTotal = (it) => (Number(it.qty || 0) * Number(it.rate || 0));
  const total = form.items.reduce((s, it) => s + lineTotal(it), 0);

  const updateItem = (idx, key, val) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, [key]: val } : it) }));
  };
  const selectItemMaterial = (idx, m) => {
    setForm(f => ({ ...f, items: f.items.map((it, i) => i === idx
      ? (m ? { ...it, material_id: m.id, description: m.name, material_code: m.material_id, unit: it.unit || m.uom || 'Nos' }
           : { ...it, material_id: '', description: '' })
      : it) }));
  };
  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { material_id: '', description: '', qty: 1, unit: 'Nos', rate: 0 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.items.some(it => it.material_id && it.qty > 0)) {
      toast.error('Add at least one line item with a Material Master item'); return;
    }
    setSaving(true);
    try {
      const vendor = vendors.find(v => v.id === form.vendor_id);
      const payload = {
        po_no: `PO-${Date.now()}`,
        vendor_id: form.vendor_id, vendor: vendor?.name || '',
        po_date: form.po_date, delivery_date: form.delivery_date,
        delivery_address: form.delivery_address,
        items: form.items, terms: form.terms,
        value: Math.round(total),
        status: 'Pending', source: 'Manual',
      };
      await apiPost('/po', payload);
      toast.success(`PO created · ${inrCompact(total)}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create PO');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-po-modal">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E]">New</div>
            <h3 className="font-display font-bold text-xl mt-1">Manual Purchase Order</h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {ff.showField('vendor_id') && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('vendor_id', 'Vendor')}{ff.reqStar('vendor_id', true)}
                </label>
                <select value={form.vendor_id} onChange={e => setForm({ ...form, vendor_id: e.target.value })} required={ff.isReq('vendor_id', true)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white" data-testid="po-vendor-select">
                  <option value="">— Select vendor —</option>
                  {vendors.map(v => (
                    <option key={v.id} value={v.id} disabled={v.incomplete}>
                      {v.name}{v.incomplete ? ' — ⚠ incomplete registration' : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {ff.showField('po_date') && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('po_date', 'PO Date')}{ff.reqStar('po_date', true)}
                </label>
                <input type="date" value={form.po_date} onChange={e => setForm({ ...form, po_date: e.target.value })} required={ff.isReq('po_date', true)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            )}
            {ff.showField('delivery_date') && (
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('delivery_date', 'Expected Delivery')}{ff.reqStar('delivery_date', true)}
                </label>
                <input type="date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} required={ff.isReq('delivery_date', true)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            )}
            {ff.showField('delivery_address') && (
              <div className="col-span-2">
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                  {ff.labelOf('delivery_address', 'Delivery Address')}{ff.reqStar('delivery_address', false)}
                </label>
                <textarea value={form.delivery_address} onChange={e => setForm({ ...form, delivery_address: e.target.value })} required={ff.isReq('delivery_address', false)} rows={2}
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            )}
          </div>

          <div className="border border-slate-200 rounded-sm overflow-visible">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <span className="kpi-overline">Line Items{ff.reqStar('items', true)}</span>
              <button type="button" onClick={addItem} data-testid="po-add-item-btn" className="text-xs text-[#1F7A6E] font-display font-bold uppercase tracking-wider hover:underline">+ Add row</button>
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50/50 text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">Material (from Master)</th>
                  <th className="text-right px-3 py-1.5 w-20">Qty</th>
                  <th className="text-left px-3 py-1.5 w-20">Unit</th>
                  <th className="text-right px-3 py-1.5 w-28">Rate (₹)</th>
                  <th className="text-right px-3 py-1.5 w-28">Total</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} className="border-t border-slate-100" data-testid={`po-item-row-${idx}`}>
                    <td className="px-2 py-1 min-w-[200px]"><MaterialPicker compact value={it.material_id} displayName={it.description} onSelect={(m) => selectItemMaterial(idx, m)} placeholder="Pick material…" testid={`po-item-material-${idx}`} /></td>
                    <td className="px-2 py-1"><input type="number" min="0" step="any" value={it.qty} onChange={e => updateItem(idx, 'qty', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="px-2 py-1"><input value={it.unit} onChange={e => updateItem(idx, 'unit', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs" /></td>
                    <td className="px-2 py-1"><input type="number" min="0" step="any" value={it.rate} onChange={e => updateItem(idx, 'rate', e.target.value)} className="w-full px-2 py-1 border border-slate-300 rounded-sm text-xs text-right tabular-nums" /></td>
                    <td className="text-right px-3 py-1 tabular-nums font-display font-semibold">{inrCompact(lineTotal(it))}</td>
                    <td className="text-center"><button type="button" onClick={() => removeItem(idx)} disabled={form.items.length === 1} className="text-rose-500 hover:text-rose-700 disabled:opacity-30"><Trash2 size={12} /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td colSpan="4" className="text-right px-3 py-2 text-[10px] font-display font-bold uppercase tracking-widest">PO Total</td>
                  <td className="text-right px-3 py-2 font-display font-bold text-base tabular-nums" data-testid="po-total">{inrCompact(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {ff.showField('terms') && (
            <div>
              <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">
                {ff.labelOf('terms', 'Terms & Conditions')}{ff.reqStar('terms', false)}
              </label>
              <textarea value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} required={ff.isReq('terms', false)} rows={3}
                placeholder="Payment terms, delivery conditions, warranty…"
                className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white font-display">Cancel</button>
          <button type="submit" disabled={saving} data-testid="manual-po-save-btn" className="px-4 py-2 text-sm bg-[#1F7A6E] hover:bg-[#14504E] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {saving ? 'Creating…' : 'Create PO'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ----- Approval Matrix -----
const ApprovalMatrix = ({ matrix }) => (
  <div>
    <div className="kpi-overline mb-3">Approval Authority Matrix</div>
    <p className="text-sm text-slate-600 mb-5 max-w-2xl">PO and CS approval is auto-routed based on the value tier below. Configure tiers in Settings → Approval Workflows.</p>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {matrix.map((t, i) => {
        const prev = i === 0 ? 0 : matrix[i-1].max;
        return (
          <div key={i} className="bg-white border border-slate-200 rounded-sm p-5 hover:border-[#1F7A6E]/40">
            <div className="kpi-overline mb-1">Tier {i+1}</div>
            <div className="font-display text-xl font-extrabold tracking-tight">{t.approver}</div>
            <div className="text-xs text-slate-600 mt-2">
              {t.max ? `${inrCompact(prev)} – ${inrCompact(t.max)}` : `Above ${inrCompact(prev)}`}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);
